/**
 * Input validation and sanitization utilities
 * Provides security-focused validation for form inputs
 */

// Sanitize string input - removes potentially dangerous characters
// Note: We don't trim here to preserve spaces during typing. Trim on submission instead.
export const sanitizeString = (input) => {
  if (typeof input !== 'string') return '';
  return input
    .replace(/[<>]/g, '') // Remove angle brackets to prevent HTML injection
    .slice(0, 1000); // Limit length to prevent DoS
};

// Sanitize for display (more aggressive)
export const sanitizeForDisplay = (input) => {
  if (typeof input !== 'string') return '';
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
};

// Validate email format
export const isValidEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email) && email.length <= 254;
};

// Validate phone number (US format)
export const isValidPhone = (phone) => {
  if (!phone) return false;
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length === 10 || (cleaned.length === 11 && cleaned.startsWith('1'));
};

export const formatPhoneNumber = (value) => {
  let digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  // Keep unsupported lengths visible so validation can catch them; never drop digits.
  if (digits.length > 10) return digits;
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// Format phone number for display
export const formatPhone = (phone) => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned[0] === '1') {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

// Validate ZIP code (US format)
export const isValidZip = (zip) => {
  if (!zip) return false;
  const zipRegex = /^\d{5}(-\d{4})?$/;
  return zipRegex.test(zip);
};

// Validate year (reasonable range)
export const isValidYear = (year, minYear = 1900, maxYear = new Date().getFullYear() + 1) => {
  const yearNum = parseInt(year, 10);
  return !isNaN(yearNum) && yearNum >= minYear && yearNum <= maxYear;
};

// Validate currency input
export const isValidCurrency = (value) => {
  if (!value) return false;
  const cleaned = value.replace(/[^0-9.]/g, '');
  const num = parseFloat(cleaned);
  return !isNaN(num) && num > 0 && num < 100000000; // Max $100M
};

// Format currency for display
export const formatCurrency = (value) => {
  const cleaned = value.replace(/[^0-9.]/g, '');
  const num = parseFloat(cleaned);
  if (isNaN(num)) return value;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

// Parse currency string to number
export const parseCurrency = (value) => {
  if (!value) return 0;
  const cleaned = value.replace(/[^0-9.]/g, '');
  return parseFloat(cleaned) || 0;
};

const parseLocalDate = (dateString) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString || '')) return null;
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
};

// Compare calendar days locally so today's date works in every timezone.
export const isValidFutureDate = (dateString) => {
  const date = parseLocalDate(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date !== null && date >= today;
};

export const isValidPastDate = (dateString) => {
  const date = parseLocalDate(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date !== null && date <= today;
};

export const deductibleOptions = ['$500', '$1,000', '$2,500', '$5,000', '$10,000', '$25,000'];
export const rentalFrequencyOptions = ['A few times per year', 'Monthly', 'Weekly', 'Daily or most days'];
export const rentalOperatorOptions = ['Without an operator', 'With an operator', 'Both'];

// Generate a secure unique ID
export const generateId = () => {
  const array = new Uint32Array(4);
  crypto.getRandomValues(array);
  return Array.from(array, (n) => n.toString(16).padStart(8, '0')).join('');
};

// Validate serial number (alphanumeric, reasonable length)
export const isValidSerialNumber = (serial) => {
  if (!serial) return false;
  const cleaned = serial.trim();
  return cleaned.length >= 3 && cleaned.length <= 50 && /^[a-zA-Z0-9\-_]+$/.test(cleaned);
};

// Validate required fields for each step
export const validateStep1 = (formData) => {
  const errors = {};
  if (!formData.legalBusinessName?.trim()) errors.legalBusinessName = 'Legal business name is required';
  if (!formData.primaryOperation) {
    errors.primaryOperation = 'Primary operation is required';
  }
  if (formData.primaryOperation === 'Other' && !formData.primaryOperationDetails?.trim()) {
    errors.primaryOperationDetails = 'Please describe your business operations';
  }
  // Contact info
  if (!formData.contactFirstName?.trim()) {
    errors.contactFirstName = 'First name is required';
  }
  if (!formData.contactLastName?.trim()) {
    errors.contactLastName = 'Last name is required';
  }
  if (!isValidEmail(formData.contactEmail)) {
    errors.contactEmail = 'Valid email is required';
  }
  if (!isValidPhone(formData.contactPhone)) {
    errors.contactPhone = 'Valid phone number is required';
  }

  if (!formData.businessState) {
    errors.businessState = 'State is required';
  }
  return errors;
};

const validateBusinessDetails = (formData) => {
  const errors = {};
  if (!formData.businessState) errors.businessState = 'State is required';
  if (!formData.businessType) {
    errors.businessType = 'Business type is required';
  }
  if (!formData.yearStarted || !isValidYear(formData.yearStarted, 1900, new Date().getFullYear())) {
    errors.yearStarted = 'Valid year started is required';
  }
  if (!formData.yearsExperience || parseInt(formData.yearsExperience) < 0) {
    errors.yearsExperience = 'Years of experience is required';
  }

  // Business address
  if (!formData.businessStreet?.trim()) {
    errors.businessStreet = 'Street address is required';
  }
  if (!formData.businessCity?.trim()) {
    errors.businessCity = 'City is required';
  }
  if (!isValidZip(formData.businessZip)) {
    errors.businessZip = 'Valid ZIP code is required';
  }
  if (!formData.businessCounty?.trim()) {
    errors.businessCounty = 'County is required';
  }

  // Mailing address (conditional)
  if (!formData.mailingDifferent) {
    errors.mailingDifferent = 'Please indicate if mailing address is different';
  }
  if (formData.mailingDifferent === 'Yes') {
    if (!formData.mailingStreet?.trim()) {
      errors.mailingStreet = 'Mailing street address is required';
    }
    if (!formData.mailingCity?.trim()) {
      errors.mailingCity = 'Mailing city is required';
    }
    if (!formData.mailingState) {
      errors.mailingState = 'Mailing state is required';
    }
    if (!isValidZip(formData.mailingZip)) {
      errors.mailingZip = 'Valid mailing ZIP code is required';
    }
    if (!formData.mailingCounty?.trim()) {
      errors.mailingCounty = 'Mailing county is required';
    }
  }

  return errors;
};

const validateInspectionContact = (formData) => {
  const errors = {};
  // Inspection contact (conditional)
  if (!formData.inspectionContactDifferent) {
    errors.inspectionContactDifferent = 'Please indicate if inspection contact is different';
  }
  if (formData.inspectionContactDifferent === 'Yes') {
    if (!formData.inspectionFirstName?.trim()) {
      errors.inspectionFirstName = 'Inspection contact first name is required';
    }
    if (!formData.inspectionLastName?.trim()) {
      errors.inspectionLastName = 'Inspection contact last name is required';
    }
    if (!isValidPhone(formData.inspectionPhone)) {
      errors.inspectionPhone = 'Valid inspection contact phone is required';
    }
  }

  return errors;
};

export const validateStep2 = (formData) => {
  const errors = {};

  if (!formData.effectiveDate) {
    errors.effectiveDate = 'Effective date is required';
  } else if (!isValidFutureDate(formData.effectiveDate)) {
    errors.effectiveDate = 'Effective date must be today or in the future';
  }
  if (!formData.hasClaimsLast3Years) {
    errors.hasClaimsLast3Years = 'Please indicate if you have had claims';
  }
  if (formData.hasClaimsLast3Years === 'Yes' && !formData.claimsDescription?.trim()) {
    errors.claimsDescription = 'Please describe the claims';
  }
  if (!deductibleOptions.includes(formData.deductiblePreference)) {
    errors.deductiblePreference = 'Please choose a deductible amount';
  }

  // Underwriting questions
  if (!formData.hasBankruptcyLast3Years) {
    errors.hasBankruptcyLast3Years = 'Please indicate bankruptcy status';
  }
  if (!formData.hasPolicyCanceledLast3Years) {
    errors.hasPolicyCanceledLast3Years = 'Please indicate policy cancellation status';
  }
  if (formData.hasPolicyCanceledLast3Years === 'Yes') {
    if (!formData.cancellationCarrier?.trim()) errors.cancellationCarrier = 'Carrier name is required';
    if (!isValidPastDate(formData.cancellationDate)) errors.cancellationDate = 'Enter a valid cancellation or nonrenewal date, today or earlier';
    if (!formData.cancellationReason?.trim()) errors.cancellationReason = 'Please describe the reason for cancellation or nonrenewal';
  }

  return errors;
};

export const validateStep3 = (formData) => {
  const errors = validateBusinessDetails(formData);

  if (!formData.storedAtBusinessAddress) {
    errors.storedAtBusinessAddress = 'Please indicate storage location';
  }
  if (formData.storedAtBusinessAddress === 'No') {
    if (!formData.storageStreet?.trim()) {
      errors.storageStreet = 'Storage street address is required';
    }
    if (!formData.storageCity?.trim()) {
      errors.storageCity = 'Storage city is required';
    }
    if (!formData.storageState) {
      errors.storageState = 'Storage state is required';
    }
    if (!isValidZip(formData.storageZip)) {
      errors.storageZip = 'Valid storage ZIP code is required';
    }
    if (!formData.storageCounty?.trim()) {
      errors.storageCounty = 'Storage county is required';
    }
  }
  if (!formData.storageType) {
    errors.storageType = 'Storage type is required';
  }
  if (!formData.siteSecurity || formData.siteSecurity.length === 0) {
    errors.siteSecurity = 'Please select at least one security option';
  }
  if (!formData.hasOperatorTrainingProgram) {
    errors.hasOperatorTrainingProgram = 'Please indicate training program status';
  }
  if (!formData.hasMaintenanceProgram) {
    errors.hasMaintenanceProgram = 'Please indicate maintenance program status';
  }

  return errors;
};

export const validateStep4 = (equipment) => {
  const errors = [];

  equipment.forEach((eq, index) => {
    const eqErrors = {};

    if (!eq.equipmentClass) {
      eqErrors.equipmentClass = 'Equipment class is required';
    }
    if (!eq.year || !isValidYear(eq.year, 1900, new Date().getFullYear() + 1)) {
      eqErrors.year = 'Valid year is required';
    }
    if (!eq.manufacturer?.trim()) {
      eqErrors.manufacturer = 'Manufacturer is required';
    }
    if (!eq.model?.trim()) {
      eqErrors.model = 'Model is required';
    }
    if (!eq.description?.trim()) {
      eqErrors.description = 'Description is required';
    }
    if (!eq.serialNumber?.trim() && eq.serialNumberPending !== true) {
      eqErrors.serialNumber = 'Enter the serial number or select “I’ll provide it later”';
    }
    if (!isValidCurrency(eq.currentValue)) {
      eqErrors.currentValue = 'Valid current value is required';
    }
    if (!eq.isFinanced) {
      eqErrors.isFinanced = 'Please indicate if equipment is financed';
    }
    if (!eq.hasTheftDeterrent) {
      eqErrors.hasTheftDeterrent = 'Please indicate theft deterrent status';
    }
    if (eq.isFinanced === 'Yes') {
      if (!eq.lienholderName?.trim()) {
        eqErrors.lienholderName = 'Lienholder name is required';
      }
      if (!eq.lienholderStreet?.trim()) {
        eqErrors.lienholderStreet = 'Lienholder street is required';
      }
      if (!eq.lienholderCity?.trim()) {
        eqErrors.lienholderCity = 'Lienholder city is required';
      }
      if (!eq.lienholderState) {
        eqErrors.lienholderState = 'Lienholder state is required';
      }
      if (!isValidZip(eq.lienholderZip)) {
        eqErrors.lienholderZip = 'Valid lienholder ZIP is required';
      }
    }

    if (Object.keys(eqErrors).length > 0) {
      errors[index] = eqErrors;
    }
  });

  return errors;
};

export const validateStep5 = (formData) => {
  const errors = validateInspectionContact(formData);

  if (!formData.hasCranesOver25ft) {
    errors.hasCranesOver25ft = 'Please indicate crane/lift status';
  }
  if (formData.hasCranesOver25ft === 'Yes') {
    if (!formData.craneDetails?.trim()) {
      errors.craneDetails = 'Please describe the cranes/lifts';
    }
    if (!formData.yearsOperatingCranes || parseInt(formData.yearsOperatingCranes) < 0) {
      errors.yearsOperatingCranes = 'Years operating cranes is required';
    }
  }

  // Risky activities checklist
  if (!formData.riskyActivities || formData.riskyActivities.length === 0) {
    errors.riskyActivities = 'Please select at least one option (or "None of the above")';
  }

  // Equipment rental
  if (!formData.equipmentRentedToOthersWithoutOperator) {
    errors.equipmentRentedToOthersWithoutOperator = 'Please indicate rental to others';
  }
  if (!formData.equipmentRentedFromOthers) {
    errors.equipmentRentedFromOthers = 'Please indicate rental from others';
  }
  if (formData.equipmentRentedToOthersWithoutOperator === 'Yes') {
    if (!formData.rentalToOthersDescription?.trim()) errors.rentalToOthersDescription = 'Describe the equipment you rent or loan to others';
    if (!isValidCurrency(formData.rentalToOthersMaxValue)) errors.rentalToOthersMaxValue = 'Enter the maximum total value rented or loaned out at one time';
    if (!rentalFrequencyOptions.includes(formData.rentalToOthersFrequency)) errors.rentalToOthersFrequency = 'Choose how often you rent or loan equipment to others';
  }
  if (formData.equipmentRentedFromOthers === 'Yes') {
    if (!formData.rentalFromOthersDescription?.trim()) errors.rentalFromOthersDescription = 'Describe the equipment you rent from others';
    if (!isValidCurrency(formData.rentalFromOthersMaxValue)) errors.rentalFromOthersMaxValue = 'Enter the maximum total value rented from others at one time';
    if (!rentalFrequencyOptions.includes(formData.rentalFromOthersFrequency)) errors.rentalFromOthersFrequency = 'Choose how often you rent equipment from others';
    if (!rentalOperatorOptions.includes(formData.rentalFromOthersOperator)) errors.rentalFromOthersOperator = 'Choose whether rented equipment comes with an operator';
  }

  return errors;
};
